import { useEffect, useState } from "react";

import { useWorkflow } from "../../workflow-context";

import { EdgeStatus, ExecutionStatus, WorkflowNodeStatus } from "@/components/Workflows/types";
import { WEBSOCKET_MESSAGE_NAME } from "@/constants";
import { useUser } from "@/context/user-context";
import { useChannel } from "@/components/Messaging/useChannel";
import { useOutputResult } from "@/queries/workflows";
import { WebSocketMessage } from "@/types/websockets";
import { displayErrorToast } from "@/utils/toast";

const useExecutionStatus = () => {
  const { user } = useUser();
  const workflow = useWorkflow();

  useChannel(
    `user:${user.id}`,
    WEBSOCKET_MESSAGE_NAME.SET_EDGE_OUTPUT,
    (socketMessage: WebSocketMessage) => {
      try {
        const { data: websocketData } = socketMessage;
        if (!websocketData) {
          return;
        }
        const data: EdgeStatus = JSON.parse(websocketData);
        const { edge_id, status } = data;
        workflow.setEdgeStatus(edge_id, status);
      } catch {
        displayErrorToast("Error processing edge output.");
      }
    },
  );

  const [outputToBeFetched, setOutputToBeFetched] = useState<
    { executionId: number; nodeId: string } | undefined
  >();

  const output = useOutputResult(outputToBeFetched?.executionId, outputToBeFetched?.nodeId);

  useEffect(() => {
    if (outputToBeFetched && output.data) {
      const data = Object.values(output.data)[0];
      workflow.addOutput(outputToBeFetched.nodeId, {
        ...data,
        date: Date.now(),
      });
      setOutputToBeFetched(undefined);
    }
  }, [outputToBeFetched, output, workflow]);

  useChannel(
    `user:${user.id}`,
    WEBSOCKET_MESSAGE_NAME.SET_WORKFLOW_NODE_OUTPUT,
    (socketMessage: WebSocketMessage) => {
      try {
        const { data: websocketData } = socketMessage;
        if (!websocketData) {
          return;
        }

        const response: ExecutionStatus = JSON.parse(websocketData);
        const { workflow_node_id, workflow_version_execution_id } = response;

        workflow.setNodeStatus(workflow_node_id, response.status);

        // eslint-disable-next-line default-case
        switch (response.status) {
          case WorkflowNodeStatus.COMPLETED:
            if (response.display_value && response.value) {
              workflow.addOutput(workflow_node_id, {
                date: Date.now(),
                display_value: response.display_value,
                request_id: response.value?.value?.request_id,
              });
            } else {
              setOutputToBeFetched({
                executionId: workflow_version_execution_id,
                nodeId: response.workflow_node_id,
              });
            }

            break;
          case WorkflowNodeStatus.QUEUED:
            break;
          case WorkflowNodeStatus.FAILED:
            workflow.setErrors([
              {
                loc: ["nodes", String(workflow.indexOf(workflow_node_id)), "node_execution_error"],
                msg: response.error_message!,
                type: "runtime",
              },
            ]);
            break;
        }
      } catch {
        displayErrorToast("Error processing file.");
      }
    },
  );
};

export default useExecutionStatus;
